import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import Select from 'react-select';
import apiProvider from "../apiProvider/product"
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const StockReportLayer = () => {
    const formatDate = (date) => {
        const d = new Date(date);
        let month = '' + (d.getMonth() + 1);
        let day = '' + d.getDate();
        const year = d.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;

        return [year, month, day].join('-');
    };

    const formatDateToDDMMYYYY = (date) => {
        if (!date) return 'N/A';
        return dayjs(date).format('DD/MM/YYYY');
    };

    const formatTimeToHHMM = (date) => {
        if (!date) return 'N/A';
        const d = new Date(date);
        if (isNaN(d.getTime())) return 'N/A';
        let hours = d.getHours();
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    };

    const today = new Date();
    const tenDaysAgo = new Date();
    tenDaysAgo.setDate(today.getDate() - 10);

    const [formData, setFormData] = useState({
        productId: "",
        fromDate: tenDaysAgo,
        toDate: today
    });
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [productList, setProductlist] = useState([]);
    const [isSearched, setIsSearched] = useState(false);

    // Pagination states
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    useEffect(() => {
        fetchProducts();
        if (isSearched) {
            fetchStockTransactionList();
        }
    }, [pageIndex]);

    const fetchProducts = async () => {
        try {
            const input = {
                page: 0,
                limit: 2000
            }
            const response = await apiProvider.getAllproductList(input);

            if (response.status) {
                setProductlist(response.response?.data || []);
            } else {
                console.error("failed to fetch product list")
            }
        } catch (error) {
            console.error("error fetching product list :", error);
        }
    }

    const fetchStockTransactionList = async () => {
        setLoading(true);
        try {
            const input = {
                page: pageIndex,
                limit: pageSize,
                productId: formData.productId,
                startDate: formatDate(formData.fromDate),
                endDate: formatDate(formData.toDate)
            }
            const response = await apiProvider.getStockTransactionList(input);

            if (response.status) {
                const data = response.response?.data || [];
                setReportData(data);
                setTotalPages(response.response?.totalPages || 1);
                setTotalRecords(response.response?.total || 0);
                setIsSearched(true);
            } else {
                console.error("failed to fetch stock transaction list")
            }
        } catch (error) {
            console.error("error fetching stock transaction list :", error);
        } finally {
            setLoading(false);
        }
    }

    const handleChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPageIndex(0);
        fetchStockTransactionList();
    };

    const handleExportExcel = () => {
        if (reportData.length === 0) {
            toast.info("No data available to export");
            return;
        }

        const dataToExport = reportData.map((item, index) => ({
            "S.No": index + 1,
            "Date": formatDateToDDMMYYYY(item.createdAt),
            "Time": formatTimeToHHMM(item.createdAt),
            "User": item.createdByName || "N/A",
            "Product Name": item.productName || "N/A",
            // "Variant": item.attributes?.variantName || "N/A",
            // "SKU": item.attributes?.sku || "N/A",
            "Type": item.type.toUpperCase(),
            "Prev Stock": item.previousStock || 0,
            "Quantity": item.quantity,
            "New Stock": (Number(item.previousStock) || 0) + (Number(item.quantity) || 0),
            // "Remarks": item.remarks
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const wscols = [
            { wch: 5 }, { wch: 12 }, { wch: 12 }, { wch: 20 }, { wch: 25 },
            { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
            { wch: 40 }
        ];
        worksheet['!cols'] = wscols;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Transaction Report");

        const fileName = `Stock_Transaction_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        toast.success("Excel report downloaded successfully!");
    };
    const primaryColor = "#1e293b";

    const customSelectStyles = {
        control: (base) => ({
            ...base,
            borderRadius: '6px',
            borderColor: '#e2e8f0',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#cbd5e1'
            }
        }),
        placeholder: (base) => ({
            ...base,
            color: '#94a3b8'
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            zIndex: 100
        }),
        option: (base, { isFocused, isSelected }) => ({
            ...base,
            backgroundColor: isSelected
                ? primaryColor
                : isFocused
                    ? '#f1f5f9'
                    : 'transparent',
            color: isSelected ? '#fff' : '#1e293b',
            cursor: 'pointer',
            padding: '10px 12px',
            '&:active': {
                backgroundColor: isSelected ? primaryColor : '#e2e8f0'
            }
        }),
        singleValue: (base) => ({
            ...base,
            color: '#1e293b'
        })
    };

    // Prepare options for react-select
    const productOptions = [
        { value: "", label: "All Products" },
        ...productList.map(p => ({
            value: p._id,
            label: `${p.productName}(${p.productCode})`
        }))
    ];

    return (
        <div className="card h-100 p-0 radius-12 overflow-hidden border shadow-sm mt-4">
            {/* <div className="card-header border-bottom bg-white ">
                <p className="mb-0 fw-bold" style={{ color: primaryColor, fontSize: '20px' }}>Stock Transaction Report</p>
            </div> */}

            <div className="card-body">
                <div className="mx-auto" style={{ maxWidth: '600px', width: '100%' }}>
                    <div className="text-center mb-20 py-3">
                        <h6 className="mb-0 fw-bold" style={{ color: primaryColor, fontSize: '18px' }}>Report Filters</h6>
                    </div>

                    <form onSubmit={handleSearch} className="bg-white p-6 radius-12">

                        <div className="mb-16">
                            <label className="form-label fw-bold text-secondary mb-4" style={{ fontSize: '14px' }}>From Date <span className="text-danger">*</span></label>
                            <DatePicker
                                selected={formData.fromDate}
                                onChange={(date) => handleChange('fromDate', date)}
                                dateFormat="dd/MM/yyyy"
                                className="form-control border-gray-200 radius-6 py-8 w-100"
                                wrapperClassName="w-100"
                                placeholderText="DD/MM/YYYY"
                                required
                            />
                        </div>
                        <div className="mb-16">
                            <label className="form-label fw-bold text-secondary mb-4" style={{ fontSize: '14px' }}>To Date <span className="text-danger">*</span></label>
                            <DatePicker
                                selected={formData.toDate}
                                onChange={(date) => handleChange('toDate', date)}
                                dateFormat="dd/MM/yyyy"
                                className="form-control border-gray-200 radius-6 py-8 w-100"
                                wrapperClassName="w-100"
                                placeholderText="DD/MM/YYYY"
                                required
                            />
                        </div>
                        <div className="mb-16">
                            <label className="form-label fw-bold text-secondary mb-4" style={{ fontSize: '14px' }}>Select Product</label>
                            <Select
                                options={productOptions}
                                value={productOptions.find(opt => opt.value === formData.productId)}
                                onChange={(option) => handleChange('productId', option.value)}
                                placeholder="Select Product"
                                styles={{
                                    ...customSelectStyles,
                                    control: (base) => ({
                                        ...customSelectStyles.control(base),
                                        minHeight: '42px',
                                        fontSize: '16px',
                                        borderRadius: '6px ',
                                    })
                                }}
                            />
                        </div>
                        <div className="text-center">
                            <button
                                type="submit"
                                className="btn w-100 py-10 radius-8 text-white fw-bold d-inline-flex align-items-center justify-content-center gap-2"
                                style={{ backgroundColor: primaryColor, border: 'none', transition: 'all 0.2s', fontSize: '14px' }}
                                disabled={loading}
                            >
                                <Icon icon="ic:baseline-search" className="text-xl" />
                                {loading ? "Searching..." : "Search Report"}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-20 mt-4">
                    <h6 className="mb-0 fw-bold">Stock Summary Results</h6>
                    <button
                        className="btn radius-8 text-white d-flex align-items-center gap-2 px-20 py-10"
                        style={{ backgroundColor: primaryColor, border: 'none' }}
                        onClick={handleExportExcel}
                        disabled={reportData.length === 0}
                    >
                        <Icon icon="mdi:file-excel" className="text-xl" />
                        Export Excel
                    </button>
                </div>

                <div
                    className="table-responsive scroll-sm border-0 radius-12 shadow-sm"
                    style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto' }}
                >
                    <table className=" mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: '1200px' }}>
                        <thead className="position-sticky top-0" style={{ zIndex: 10 }}>
                            <tr style={{ backgroundColor: primaryColor }}>
                                <th scope="col" className="text-white fw-bold py-16 px-12 border-0 text-center" style={{ fontSize: '13px' }}>S.No</th>
                                <th scope="col" className="text-white fw-bold py-16 px-12 border-0" style={{ fontSize: '13px' }}>Date & Time</th>
                                <th scope="col" className="text-white fw-bold py-16 px-12 border-0" style={{ fontSize: '13px' }}>User</th>
                                <th scope="col" className="text-white fw-bold py-16 px-12 border-0" style={{ fontSize: '13px' }}>Product Name</th>
                                {/* <th scope="col" className="text-white fw-bold py-16 px-12 border-0" style={{ fontSize: '13px' }}>Variant</th> */}
                                {/* <th scope="col" className="text-white fw-bold py-16 px-12 border-0" style={{ fontSize: '13px' }}>SKU</th> */}
                                <th scope="col" className="text-white fw-bold py-16 px-12 border-0 text-center" style={{ fontSize: '13px' }}>Type</th>
                                <th scope="col" className="text-white fw-bold py-16 px-12 border-0 text-center" style={{ fontSize: '13px' }}>Prev Stock</th>
                                <th scope="col" className="text-white fw-bold py-16 px-12 border-0 text-center" style={{ fontSize: '13px' }}>Quantity</th>
                                <th scope="col" className="text-white fw-bold py-16 px-12 border-0 text-center" style={{ fontSize: '13px' }}>New Stock</th>
                                {/* <th scope="col" className="text-white fw-bold py-16 px-12 border-0" style={{ fontSize: '13px' }}>Remarks</th> */}
                            </tr>
                        </thead>
                        <tbody className="bg-white">
                            {isSearched && reportData.length > 0 ? (
                                reportData.map((item, index) => {
                                    const prevStock = Number(item.previousStock) || 0;
                                    const change = Number(item.quantity) || 0;
                                    const newStock = prevStock + change;
                                    const dateObj = new Date(item.createdAt);

                                    return (
                                        <tr key={index} className="border-bottom hover-bg-light" style={{ transition: 'background 0.2s' }}>
                                            <td className="py-12 px-12 text-secondary text-center" style={{ fontSize: '13px' }}>{pageIndex * pageSize + index + 1}</td>
                                            <td className="py-12 px-12">
                                                <div className="fw-medium text-dark" style={{ fontSize: '13px' }}>{formatDateToDDMMYYYY(dateObj)}</div>
                                                <div className="text-muted" style={{ fontSize: '11px' }}>{formatTimeToHHMM(dateObj)}</div>
                                            </td>
                                            <td className="py-12 px-12 fw-medium text-dark" style={{ fontSize: '13px' }}>{item.createdByName || "N/A"}</td>
                                            <td className="py-12 px-12 fw-bold text-dark" style={{ fontSize: '13px' }}>{item.productName || "N/A"}</td>
                                            {/* <td className="py-12 px-12">
                                                <div className="fw-medium text-info-main" style={{ fontSize: '13px' }}>
                                                    {item.attributes?.variantName ||
                                                        item.attributes?.sizeCode ||
                                                        (item.attributes ? Object.entries(item.attributes)
                                                            .filter(([k, v]) => !['sku', '_id', 'pricing'].includes(k) && typeof v !== 'object')
                                                            .pop()?.[1] || "N/A" : "N/A")}
                                                </div>
                                            </td> */}
                                            {/* <td className="py-12 px-12 text-secondary" style={{ fontSize: '13px' }}>{item.attributes?.sku || "N/A"}</td> */}
                                            <td className="py-12 px-12 text-center">
                                                <span className={`badge radius-4 px-10 py-4 ${item.type === 'order' ? 'bg-info-100 text-info-main' :
                                                    item.type === 'adjustment' ? 'bg-warning-100 text-warning-main' :
                                                        'bg-success-100 text-success-main'
                                                    }`} style={{ fontSize: '11px' }}>
                                                    {item.type.toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="py-12 px-12 text-center text-secondary fw-medium" style={{ fontSize: '13px' }}>{prevStock}</td>
                                            <td className={`py-12 px-12 text-center fw-bold ${change >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '13px' }}>
                                                {change > 0 ? `+${change}` : change}
                                            </td>
                                            <td className="py-12 px-12 text-center fw-bold text-dark" style={{ fontSize: '13px' }}>{newStock}</td>
                                            {/* <td className="py-12 px-12 text-secondary" style={{ maxWidth: '250px', fontSize: '12px', lineHeight: '1.4' }}>{item.remarks}</td> */}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="11" className="text-center py-60 text-muted bg-white">
                                        <div className="d-flex flex-column align-items-center opacity-50">
                                            <Icon icon="mdi:database-search-outline" style={{ fontSize: '48px' }} className="mb-12" />
                                            {isSearched ? "No matching records found." : "Configure filters and search to generate your report."}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* {isSearched && reportData.length > 0 && (
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24 px-12 pb-12">
                        <span className="text-secondary-light">
                            Showing {pageIndex * pageSize + 1} to {Math.min((pageIndex + 1) * pageSize, totalRecords)} of {totalRecords} entries
                        </span>
                        <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center mb-0">
                            <li className={`page-item ${pageIndex === 0 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md pagination-btn-hover"
                                    onClick={() => setPageIndex(p => Math.max(0, p - 1))}
                                    disabled={pageIndex === 0}
                                    style={{ cursor: pageIndex === 0 ? 'not-allowed' : 'pointer', border: 'none', transition: 'all 0.2s' }}
                                >
                                    <Icon icon="ep:d-arrow-left" />
                                </button>
                            </li>

                            {[...Array(totalPages)].map((_, i) => {
                                // Logic: Show only two pages. 
                                // Either (current and next) or (prev and last if on the end)
                                let shouldShow = false;
                                if (totalPages <= 2) {
                                    shouldShow = true;
                                } else if (pageIndex === totalPages - 1) {
                                    // If last page, show last-1 and last
                                    shouldShow = (i === totalPages - 2 || i === totalPages - 1);
                                } else {
                                    // Otherwise show current and next
                                    shouldShow = (i === pageIndex || i === pageIndex + 1);
                                }

                                if (shouldShow) {
                                    return (
                                        <li key={i} className="page-item">
                                            <button
                                                className={`page-link fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md transition-all ${pageIndex === i ? 'bg-primary-600 text-white pagination-btn-active' : 'bg-neutral-200 text-secondary-light pagination-btn-hover'}`}
                                                onClick={() => setPageIndex(i)}
                                                style={{ border: 'none', transition: 'all 0.2s' }}
                                            >
                                                {i + 1}
                                            </button>
                                        </li>
                                    );
                                }
                                return null;
                            })}

                            <li className={`page-item ${pageIndex + 1 >= totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md pagination-btn-hover"
                                    onClick={() => setPageIndex(p => Math.min(totalPages - 1, p + 1))}
                                    disabled={pageIndex + 1 >= totalPages}
                                    style={{ cursor: pageIndex + 1 >= totalPages ? 'not-allowed' : 'pointer', border: 'none', transition: 'all 0.2s' }}
                                >
                                    <Icon icon="ep:d-arrow-right" />
                                </button>
                            </li>
                        </ul>
                    </div>
                )} */}
                {isSearched && reportData.length > 0 && totalPages > 1 && (
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24 px-12 pb-12">
                        <span className="text-secondary-light" style={{ fontSize: '13px' }}>
                            Showing {pageIndex * pageSize + 1} to {Math.min((pageIndex + 1) * pageSize, totalRecords)} of {totalRecords} entries
                        </span>
                        <div className="d-flex align-items-center gap-3">
                            <button
                                onClick={() => setPageIndex(p => Math.max(0, p - 1))}
                                disabled={pageIndex === 0}
                                style={{
                                    background: "#fff",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "8px",
                                    padding: "8px 18px",
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    color: pageIndex === 0 ? "#adb5bd" : primaryColor,
                                    cursor: pageIndex === 0 ? "not-allowed" : "pointer",
                                    opacity: pageIndex === 0 ? 0.5 : 1,
                                    transition: "all 0.2s"
                                }}
                            >
                                ‹ Previous
                            </button>
                            <span style={{ fontSize: "13px", color: "#6c757d" }}>
                                Page <b style={{ color: "#1e293b" }}>{pageIndex + 1}</b> of {totalPages}
                            </span>
                            <button
                                onClick={() => setPageIndex(p => Math.min(totalPages - 1, p + 1))}
                                disabled={pageIndex + 1 >= totalPages}
                                style={{
                                    background: pageIndex + 1 >= totalPages ? "#adb5bd" : primaryColor,
                                    border: "none",
                                    borderRadius: "8px",
                                    padding: "8px 18px",
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    color: "#fff",
                                    cursor: pageIndex + 1 >= totalPages ? "not-allowed" : "pointer",
                                    opacity: pageIndex + 1 >= totalPages ? 0.6 : 1,
                                    transition: "all 0.2s"
                                }}
                            >
                                Next ›
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <ToastContainer />
        </div>
    );
};

export default StockReportLayer;
